import { useSwipeable } from 'react-swipeable'
import React, { useState } from 'react'
import { Trans } from '@lingui/macro'
import { Spinner } from '@chakra-ui/react'
import ReactGA from 'react-ga'

import { useLogout } from 'front-end/src/public-api/hooks/useLogout'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'
import IconMap from '../common/icon/IconMap'
import { staticLinks } from 'shared/linkBuilder'
import { IconDashboardSvg } from '../common/icon/IconDashboard'
import { Box, LinkOverlay, LinkBox, Flex } from 'front-end/src/components'

import IconMenu from '../common/icon/IconMenu'
import IconQuestion from '../common/icon/IconQuestion'
import IconLogout from '../common/icon/IconLogout'
import IconHome from '../common/icon/IconHome'
import IconBubbles from '../common/icon/IconBubbles'
import IconGraph from '../common/icon/IconGraph'
import IconCalendar from '../common/icon/IconCalendar'
import IconEnergy from '../common/icon/IconEnergy'
import IconBadge from '../common/icon/IconBadge'
import IconSettings from '../common/icon/IconSettings'

import './Navigation.less'
import { IconHistory } from '../common/icon/IconHistory'
import { AdminOnlyAllowed } from '../common/AdminOnlyAllowed'
import { HideOnMobileDevice } from '../common/HideOnMobileDevice'
import { isLocaleWithKanjiLetters } from 'front-end/src/utils/checkLocale'
import { NavigationWorkspaces } from 'front-end/src/components/navigation/NavigationWorkspaces'

export const Navigation = () => {
  const [navigationOpen, setNavigationOpen] = useState(false)
  const hideNavigation = () => setNavigationOpen(false)
  const logout = useLogout()
  const handlers = useSwipeable({
    onSwipedLeft: hideNavigation
  })
  const { session } = usePublicApiSession()
  const { featureFlags, loadingFeatureFlags } = useFeatureFlags()

  return (
    <>
      <LinkBox
        className="Navigation__menuToggle"
        h={['56px', '64px']}
        w={isLocaleWithKanjiLetters() ? '48px' : undefined}
        onClick={() => {
          setNavigationOpen(true)
        }}
        aria-expanded={navigationOpen}
      >
        <IconMenu role="img" aria-label="Menu" />
        <Box
          mt="2px"
          className="Navigation__menuToggle__text"
          display={isLocaleWithKanjiLetters() ? 'none' : undefined}
        >
          <Trans>Menu</Trans>
        </Box>
      </LinkBox>
      <Box
        userSelect="none"
        className={`Navigation__wrapper ${navigationOpen ? 'open' : ''}`}
        {...handlers}
      >
        <Box className="Navigation__mask" onClick={hideNavigation} />
        <Box as="nav" className="Navigation">
          <Box className="Navigation__section">
            <Flex className="Navigation__link__wrapper">
              <LinkOverlay
                className="Navigation__link"
                to="/"
                onClick={hideNavigation}
              >
                <IconHome /> <Trans>Home</Trans>
              </LinkOverlay>
            </Flex>
            {featureFlags?.dashboards && (
              <AdminOnlyAllowed>
                <Flex className="Navigation__link__wrapper Navigation__dashboards">
                  <LinkOverlay
                    className="Navigation__link"
                    to={staticLinks.dashboards}
                    onClick={hideNavigation}
                  >
                    <Box className="Icon IconDashboard">
                      <IconDashboardSvg />
                    </Box>
                    <Trans>Dashboards</Trans>
                  </LinkOverlay>
                </Flex>
              </AdminOnlyAllowed>
            )}
            <Flex className="Navigation__link__wrapper Navigation__pathways">
              <LinkOverlay
                className="Navigation__link"
                to={staticLinks.pathways}
                onClick={hideNavigation}
              >
                <IconMap />
                <Trans>Pathways</Trans>
              </LinkOverlay>
            </Flex>
            <Flex className="Navigation__link__wrapper Navigation__events">
              <LinkOverlay
                className="Navigation__link"
                to={staticLinks.events}
                onClick={hideNavigation}
              >
                <IconCalendar /> <Trans>Events</Trans>
              </LinkOverlay>
            </Flex>
            <AdminOnlyAllowed>
              <Flex className="Navigation__link__wrapper Navigation__surveys">
                <LinkOverlay
                  className="Navigation__link"
                  to="/surveys"
                  onClick={hideNavigation}
                >
                  <IconBubbles /> <Trans>Surveys</Trans>
                </LinkOverlay>
              </Flex>
            </AdminOnlyAllowed>
            {session?.user.canViewAnalytics && (
              <HideOnMobileDevice>
                <Flex className="Navigation__link__wrapper Navigation__analytics">
                  <LinkOverlay
                    className="Navigation__link"
                    to="/analytics/people"
                    onClick={hideNavigation}
                  >
                    <IconGraph />
                    <Trans>Analytics</Trans>
                  </LinkOverlay>
                </Flex>
              </HideOnMobileDevice>
            )}
            {featureFlags?.campaigns && (
              <AdminOnlyAllowed>
                <Flex className="Navigation__link__wrapper Navigation__campaigns">
                  <LinkOverlay
                    className="Navigation__link"
                    to={staticLinks.campaigns}
                    onClick={hideNavigation}
                  >
                    <IconEnergy />
                    <Trans>Campaigns</Trans>
                  </LinkOverlay>
                </Flex>
              </AdminOnlyAllowed>
            )}
            {loadingFeatureFlags && <Spinner />}
            {featureFlags?.certificates && (
              <Flex className="Navigation__link__wrapper Navigation__certifications">
                <LinkOverlay
                  className="Navigation__link"
                  to={staticLinks.certifications}
                  onClick={hideNavigation}
                >
                  <IconBadge />
                  <Trans>Certifications</Trans>
                </LinkOverlay>
              </Flex>
            )}
            <Flex className="Navigation__link__wrapper">
              <LinkOverlay
                className="Navigation__link"
                to={staticLinks.activityHistory}
                onClick={() => {
                  ReactGA.event({
                    category: 'Menu',
                    action: 'Click',
                    label: 'Activity history'
                  })
                  setNavigationOpen(false)
                }}
              >
                <IconHistory /> <Trans>Activity history</Trans>
              </LinkOverlay>
            </Flex>
          </Box>

          <Box className="Navigation__section">
            <Box className="Navigation__section">
              <NavigationWorkspaces hideNavigation={hideNavigation} />
            </Box>

            <Box className="Navigation__section Navigation__user">
              <Flex className="Navigation__link__wrapper">
                <LinkOverlay
                  className="Navigation__link"
                  to="/user"
                  onClick={hideNavigation}
                >
                  <IconSettings /> <Trans>Profile settings</Trans>
                </LinkOverlay>
              </Flex>
              <AdminOnlyAllowed>
                <Flex className="Navigation__link__wrapper">
                  <LinkOverlay
                    className="Navigation__link"
                    to="http://support.looop.co"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={hideNavigation}
                  >
                    <IconQuestion /> <Trans>Support</Trans>
                  </LinkOverlay>
                </Flex>
              </AdminOnlyAllowed>
              <Flex className="Navigation__link__wrapper">
                <Box
                  as="a"
                  className="Navigation__link"
                  onClick={() => {
                    logout(session?.session_attributes_data.ssoLogoutURL || '')
                  }}
                >
                  <IconLogout /> <Trans>Log out</Trans>
                </Box>
              </Flex>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}
